import DatePicker from "@/components/date-picker";
import { CustomPopover } from "@/components/popover/CustomPopover";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import { EntityTypes, TipoOperacionSolicitudEstablecimiento } from "@/constants/enums";
import {
  getOptionsHandler,
  loadOptionsHandler,
} from "@/pages/grain-requests/future-contract/schema/handler-functions";
import {
  EntityStatusOption,
  getStatusByEntity,
} from "@/services/entity-status.service";
import { AppColors } from "@/styles/colors";
import { Badge, Box, Button, Field, Flex, Grid, GridItem, HStack, Text } from "@chakra-ui/react";
import { Dayjs } from "dayjs";
import { Filter } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Control, FieldValues, UseFormHandleSubmit } from "react-hook-form";

const TIPO_OPERACION_OPTIONS = [
  { label: "Alta", value: TipoOperacionSolicitudEstablecimiento.Alta },
  { label: "Modificación", value: TipoOperacionSolicitudEstablecimiento.Modificacion },
  { label: "Baja", value: TipoOperacionSolicitudEstablecimiento.Baja },
];

interface Props {
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => void;
  clearFilters: () => void;
  control: Control<FieldValues>;
  filtersApplied?: boolean;
  disableApply?: boolean;
  disableFilters?: boolean;
  maxDate?: Dayjs;
}

export const EstablishmentFieldRequestFilter = ({
  handleSubmit,
  openFilters,
  setOpenFilters,
  submitFilters,
  clearFilters,
  control,
  filtersApplied,
  disableApply,
  disableFilters,
  maxDate,
}: Props) => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const [statusOptions, setStatusOptions] = useState<EntityStatusOption[]>([]);
  const [loadingStatusOptions, setLoadingStatusOptions] = useState(false);

  const fetchStatusOptions = async () => {
    setLoadingStatusOptions(true);
    try {
      const response = await getStatusByEntity({ Id: EntityTypes.SolicitudAgrobit });
      setStatusOptions(response ?? []);
    } finally {
      setLoadingStatusOptions(false);
    }
  };

  useEffect(() => {
    if (isActive) fetchStatusOptions();
  }, [isActive]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  const getOptions = async (inputValue: string) =>
    getOptionsHandler({ inputValue });

  const loadOptions = (inputValue: string): Promise<Option[]> =>
    loadOptionsHandler({
      getOptions,
      inputValue,
      setLoadingPersons: setLoadingAccounts,
      timeoutRef,
    });

  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters}
              >
                <Filter />
                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>
              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>
                <Button
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={disableFilters}
                >
                  Limpiar filtros
                </Button>
              </Flex>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <DatePicker
                    control={control}
                    labelText="Fecha"
                    name="fecha"
                    placeholder="Ingresar fecha"
                    maxDate={maxDate}
                  />
                </GridItem>

                <GridItem maxWidth="250px">
                  <Field.Root>
                    <Field.Label>
                      <Text fontWeight={600}>Titular cuenta</Text>
                    </Field.Label>
                    <AsyncAutocompleteSelect
                      control={control}
                      loadOptions={loadOptions}
                      name="account_number"
                      placeholder="Buscar..."
                      isLoading={loadingAccounts}
                      disabled={disableFilters || loadingAccounts}
                    />
                  </Field.Root>
                </GridItem>

                <GridItem>
                  <SelectComponent
                    name="tipo_operacion"
                    items={TIPO_OPERACION_OPTIONS}
                    labelText="Tipo de operación"
                    placeholder="Seleccionar"
                    control={control}
                    disabled={disableFilters}
                    menuMaxHeight="200px"
                  />
                </GridItem>

                <GridItem>
                  <SelectComponent
                    name="estados"
                    items={statusOptions.map((s) => ({
                      label: s.nombre,
                      value: s.ids?.[0] ?? "",
                    }))}
                    labelText="Estado"
                    placeholder="Seleccionar"
                    control={control}
                    loading={loadingStatusOptions}
                    disabled={loadingStatusOptions || disableFilters}
                    multiple
                    menuMaxHeight="250px"
                  />
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                disabled={disableFilters || disableApply}
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
